/**
 * @description This hook is used to fetch the chat channels from the API.
 * @returns A query object with the channels data.
 * @reactquery No cache (staleTime: 0, gcTime: 0) - Real-time updates handled by Pusher
**/

import { useQuery } from "@tanstack/react-query";
import { useChatVersion } from "@/features/chat/hooks/useChatVersion";
import { chatLegacyService } from "@/features/chat/services/chat.legacy.service";
import { chatService } from "@/features/chat/services/chat.service";
import type { ChatContextType } from "@/features/chat/types";

type UseChannelsOptions = { enabled?: boolean, contextType?: ChatContextType };

export const useChannels = ({ enabled = true, contextType }: UseChannelsOptions = {}) => {
  const version = useChatVersion();
  const service = version === "legacy" ? chatLegacyService : chatService;

  const query = useQuery({
    queryKey: ["channels", contextType ?? null],
    queryFn: () => service.getChannels({ contextType }),
    staleTime: 0,
    gcTime: 0,
    meta: { errorConfig: { silent: true } },
    enabled,
  });

  return query;
};
